import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, Trash2, X, Filter, Calendar } from 'lucide-react';
import { followUps as followUpsApi, adminCustomers } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────
const PRIORITY_BADGE = {
  high:   'bg-danger',
  normal: 'bg-primary',
  low:    'bg-secondary',
};

const STATUS_BADGE = {
  open:      'bg-success',
  completed: 'bg-dark',
  snoozed:   'bg-warning text-dark',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function isOverdue(fu) {
  if (fu.status !== 'open') return false;
  return fu.dueDate < new Date().toISOString().slice(0, 10);
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AllFollowUps() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const isAdmin   = user?.role === 'admin';

  // ── Filter bar (controlled) vs applied snapshot ───────────────────────────
  const [filters, setFilters]   = useState({ status: 'open', dueDate: '', repID: '' });
  const [applied, setApplied]   = useState({ status: 'open', dueDate: '', repID: '' });

  const [followUps, setFollowUps] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [reps,      setReps]      = useState([]);

  // ── Per-row action state ──────────────────────────────────────────────────
  const [completing,   setCompleting]   = useState({});
  const [snoozeTarget, setSnoozeTarget] = useState(null); // follow-up object
  const [snoozeDate,   setSnoozeDate]   = useState('');
  const [snoozing,     setSnoozing]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // follow-up object
  const [deleting,     setDeleting]     = useState(false);
  const [error,        setError]        = useState('');

  // ── Load reps (admin only) ────────────────────────────────────────────────
  useEffect(() => {
    if (isAdmin) {
      adminCustomers.reps()
        .then(res => setReps(res.data.data || []))
        .catch(() => {});
    }
  }, [isAdmin]);

  // ── Fetch follow-ups whenever applied filters change ──────────────────────
  const fetchFollowUps = useCallback(async (f) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (f.status  && f.status  !== 'all') params.status  = f.status;
      else if (f.status === 'all')          params.status  = 'all';
      if (f.dueDate) params.dueDate = f.dueDate;
      if (f.repID)   params.repID   = f.repID;
      const res = await followUpsApi.listAll(params);
      setFollowUps(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load follow-ups.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFollowUps(applied); }, [applied, fetchFollowUps]);

  const handleApply = () => setApplied({ ...filters });

  const handleReset = () => {
    const reset = { status: 'open', dueDate: '', repID: '' };
    setFilters(reset);
    setApplied(reset);
  };

  // ── Mark complete ─────────────────────────────────────────────────────────
  const handleComplete = async (fu) => {
    setCompleting(prev => ({ ...prev, [fu.followUpID]: true }));
    setError('');
    try {
      await followUpsApi.update(fu.customerID, fu.followUpID, { status: 'completed' });
      setFollowUps(prev => prev.map(f =>
        f.followUpID === fu.followUpID ? { ...f, status: 'completed' } : f
      ));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete follow-up.');
    } finally {
      setCompleting(prev => { const n = { ...prev }; delete n[fu.followUpID]; return n; });
    }
  };

  // ── Snooze ────────────────────────────────────────────────────────────────
  const handleSnooze = async () => {
    if (!snoozeDate || !snoozeTarget) return;
    setSnoozing(true);
    setError('');
    try {
      await followUpsApi.update(snoozeTarget.customerID, snoozeTarget.followUpID, {
        status: 'snoozed', snoozedUntil: snoozeDate,
      });
      setFollowUps(prev => prev.map(f =>
        f.followUpID === snoozeTarget.followUpID
          ? { ...f, status: 'snoozed', snoozedUntil: snoozeDate }
          : f
      ));
      setSnoozeTarget(null);
      setSnoozeDate('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to snooze follow-up.');
    } finally {
      setSnoozing(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      await followUpsApi.delete(deleteTarget.customerID, deleteTarget.followUpID);
      setFollowUps(prev => prev.filter(f => f.followUpID !== deleteTarget.followUpID));
      setDeleteTarget(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete follow-up.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-0">Follow-Ups</h4>
          <small className="text-muted">Track and manage scheduled follow-ups across all accounts</small>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2 d-flex align-items-center justify-content-between mb-3">
          <span>{error}</span>
          <button className="btn-close btn-sm" onClick={() => setError('')} aria-label="Dismiss" />
        </div>
      )}

      {/* Filter bar */}
      <div className="card mb-4">
        <div className="card-body py-3">
          <div className="row g-3 align-items-end">
            <div className="col-sm-3 col-md-2">
              <label className="form-label small fw-semibold mb-1">Status</label>
              <select
                className="form-select form-select-sm"
                value={filters.status}
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              >
                <option value="open">Open</option>
                <option value="completed">Completed</option>
                <option value="snoozed">Snoozed</option>
                <option value="all">All</option>
              </select>
            </div>
            <div className="col-sm-3 col-md-2">
              <label className="form-label small fw-semibold mb-1">Due By</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.dueDate}
                onChange={e => setFilters(f => ({ ...f, dueDate: e.target.value }))}
              />
            </div>
            {isAdmin && (
              <div className="col-sm-4 col-md-3">
                <label className="form-label small fw-semibold mb-1">Rep</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.repID}
                  onChange={e => setFilters(f => ({ ...f, repID: e.target.value }))}
                >
                  <option value="">All Reps</option>
                  {reps.map(r => (
                    <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-sm-auto d-flex gap-2">
              <button
                className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                onClick={handleApply}
              >
                <Filter size={13} /> Apply
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : followUps.length === 0 ? (
        <div className="text-center text-muted py-5 border rounded bg-white">
          <Calendar size={40} className="mb-2 opacity-50" />
          <p className="mb-0">No follow-ups match the current filters.</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover table-striped align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 90 }}>Priority</th>
                  <th>Account</th>
                  <th>Reminder Note</th>
                  <th style={{ width: 130 }}>Due Date</th>
                  <th style={{ width: 100 }}>Status</th>
                  {isAdmin && <th style={{ width: 120 }}>Rep</th>}
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {followUps.map(fu => {
                  const overdue = isOverdue(fu);
                  return (
                    <tr key={fu.followUpID}>
                      <td>
                        <span className={`badge text-capitalize ${PRIORITY_BADGE[fu.priority] || 'bg-secondary'}`}>
                          {fu.priority}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-link btn-sm p-0 text-start fw-semibold"
                          onClick={() => navigate(`/admin/customers/${fu.customerID}`)}
                        >
                          {fu.companyName}
                        </button>
                      </td>
                      <td className="small">{fu.reminderNote}</td>
                      <td className={`small ${overdue ? 'text-danger fw-semibold' : ''}`}>
                        {fmtDate(fu.dueDate)}
                        {overdue && <span className="d-block" style={{ fontSize: '0.7rem' }}>Overdue</span>}
                        {fu.status === 'snoozed' && fu.snoozedUntil && (
                          <span className="d-block text-muted" style={{ fontSize: '0.7rem' }}>
                            Snoozed → {fmtDate(fu.snoozedUntil)}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge text-capitalize ${STATUS_BADGE[fu.status] || 'bg-secondary'}`}>
                          {fu.status}
                        </span>
                      </td>
                      {isAdmin && <td className="small text-muted">{fu.repName || '—'}</td>}
                      <td>
                        <div className="d-flex gap-1">
                          {fu.status === 'open' && (
                            <>
                              <button
                                className="btn btn-success btn-sm py-0 px-1"
                                title="Mark complete"
                                onClick={() => handleComplete(fu)}
                                disabled={!!completing[fu.followUpID]}
                              >
                                {completing[fu.followUpID]
                                  ? <span className="spinner-border" style={{ width: 12, height: 12, borderWidth: 2 }} role="status" aria-hidden="true" />
                                  : <CheckCircle2 size={13} />}
                              </button>
                              <button
                                className="btn btn-outline-warning btn-sm py-0 px-1"
                                title="Snooze"
                                onClick={() => { setSnoozeTarget(fu); setSnoozeDate(''); }}
                              >
                                <Clock size={13} />
                              </button>
                            </>
                          )}
                          <button
                            className="btn btn-outline-danger btn-sm py-0 px-1"
                            title="Delete"
                            onClick={() => setDeleteTarget(fu)}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="card-footer text-muted small py-2">
            {followUps.length} follow-up{followUps.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* ── Snooze modal ── */}
      {snoozeTarget && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-header py-3">
                  <h6 className="modal-title d-flex align-items-center gap-2">
                    <Clock size={16} /> Snooze Follow-Up
                  </h6>
                  <button
                    className="btn-close"
                    onClick={() => setSnoozeTarget(null)}
                    disabled={snoozing}
                  />
                </div>
                <div className="modal-body py-3">
                  <p className="small text-muted mb-2">
                    <strong>{snoozeTarget.companyName}</strong> — {snoozeTarget.reminderNote}
                  </p>
                  <label className="form-label small fw-semibold">Snooze until</label>
                  <input
                    type="date"
                    className="form-control"
                    value={snoozeDate}
                    min={todayStr()}
                    onChange={e => setSnoozeDate(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="modal-footer py-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setSnoozeTarget(null)}
                    disabled={snoozing}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={handleSnooze}
                    disabled={!snoozeDate || snoozing}
                  >
                    {snoozing
                      ? <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                      : null}
                    Snooze
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => !snoozing && setSnoozeTarget(null)} />
        </>
      )}

      {/* ── Delete confirm modal ── */}
      {deleteTarget && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <Trash2 size={32} className="text-danger mb-2" />
                  <p className="mb-0 fw-semibold">Delete this follow-up?</p>
                  <p className="text-muted small mt-1 mb-0">
                    <strong>{deleteTarget.companyName}</strong> — {deleteTarget.reminderNote}
                  </p>
                </div>
                <div className="modal-footer justify-content-center border-0 pt-0">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setDeleteTarget(null)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => !deleting && setDeleteTarget(null)} />
        </>
      )}
    </div>
  );
}
